import { useMemo, useState } from "react";
import { normalizeItemSearchText } from "../../../utils/itemSearchText";

/**
 * Assigned-inventory rows for the selected employee + filter state for the signed-items table.
 */
export const useSignatureAssignedItemsPanelState = ({
  selectedEmployee,
  inventory,
  items,
  isChildUser,
  currentUser,
}) => {
  const assignedItems = useMemo(() => {
    if (!selectedEmployee || !inventory || !items) return [];

    return inventory
      .filter((item) => {
        if (isChildUser && currentUser.employeeId) {
          const firstAssignment =
            item.assignments?.first?.[currentUser.employeeId];
          const secondAssignment =
            item.assignments?.second?.[selectedEmployee.personalNumber];

          return (
            firstAssignment &&
            firstAssignment.quantity > 0 &&
            secondAssignment &&
            secondAssignment.quantity > 0
          );
        }
        const firstAssignment =
          item.assignments?.first?.[selectedEmployee.personalNumber];
        return firstAssignment && firstAssignment.quantity > 0;
      })
      .map((item) => {
        let assignment;
        let assignedQuantity = 0;
        let assignedAt = null;

        if (isChildUser && currentUser.employeeId) {
          assignment =
            item.assignments?.second?.[selectedEmployee.personalNumber];
          assignedQuantity = assignment?.quantity || 0;
          assignedAt = assignment?.assignedAt;
        } else {
          assignment =
            item.assignments?.first?.[selectedEmployee.personalNumber];
          assignedQuantity = assignment?.quantity || 0;
          assignedAt = assignment?.assignedAt;
        }

        const itemDefinition = items.find(
          (i) => i.id === item.itemDefinitionId,
        );

        let availableQuantity = 0;
        if (isChildUser && currentUser.employeeId && !item.serialNumber) {
          const firstAssignment =
            item.assignments?.first?.[currentUser.employeeId];
          if (firstAssignment && firstAssignment.quantity > 0) {
            const firstQuantity = firstAssignment.quantity || 0;
            const secondAssignments = item.assignments?.second || {};
            const secondSum = Object.values(secondAssignments).reduce(
              (sum, a) => sum + (a.quantity || 0),
              0,
            );
            availableQuantity = Math.max(0, firstQuantity - secondSum);
          }
        } else if (!isChildUser && !item.serialNumber) {
          availableQuantity = item.availableQuantity ?? 0;
        }

        return {
          ...item,
          assignedQuantity,
          assignedAt,
          itemName: itemDefinition?.itemName || "Unknown Item",
          isSerialItem: !!item.serialNumber,
          availableQuantity,
        };
      });
  }, [selectedEmployee, inventory, items, isChildUser, currentUser]);

  const [assignedItemsFilter, setAssignedItemsFilter] = useState("");

  const filteredAssignedItems = useMemo(() => {
    if (!assignedItemsFilter.trim()) return assignedItems;

    const filterTerm = normalizeItemSearchText(assignedItemsFilter).trim();
    return assignedItems.filter((item) => {
      const itemName = normalizeItemSearchText(item.itemName);
      const serialNumber = normalizeItemSearchText(item.serialNumber);
      return itemName.includes(filterTerm) || serialNumber.includes(filterTerm);
    });
  }, [assignedItems, assignedItemsFilter]);

  return {
    assignedItems,
    assignedItemsFilter,
    setAssignedItemsFilter,
    filteredAssignedItems,
  };
};
